import React, { useEffect, useState, useRef } from "react";
import { Button, Card, Col, Form, Input, Row, Select, SelectProps } from "antd";
import { useParams } from "react-router-dom";
import { Toast } from 'primereact/toast';
import HTTPService from "../../../utils/makerequest";
import { ICompanyData } from "../../../interfaces/ICompanyData";
import { IAuthentication, UserRole } from "../../../interfaces/authentication";
import { StateLoader } from "../../../redux/StateLoader";
import { useTranslation } from 'react-i18next';
import { ILocationData } from "../../../interfaces/ILocationData";
import { ICampusData } from "../../../interfaces/ICampusData";
import { IPageProps } from "../../../interfaces/page-data";
import { SyncOutlined, CloseOutlined } from "@ant-design/icons";

const UpdateLocation: React.FunctionComponent<IPageProps> = () => {
  const toast = useRef<any>(null);
  const { locationId } = useParams<any>();
  const [locationData, setLocationData] = useState<ILocationData>();
  const [campusData, setCampusData] = useState<ICampusData[]>([]);
  const [companyData, setCompanyData] = useState<ICompanyData[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<number | undefined>(undefined);
  const [selectedCampus, setSelectedCampus] = useState<number | undefined>(undefined);
  const [form] = Form.useForm(); // Form instance
  const loader = new StateLoader();
  const userInfo: IAuthentication = loader.loadAuthState();
  const { t } = useTranslation();

  useEffect(() => {
    fetchCompanyData();
    if (locationId) {
      getLocationData(locationId);
    }
  }, [locationId]);

  // useEffect(() => {
  //   if (locationData) {
  //     form.setFieldsValue({
  //       locationName: locationData.locationName,
  //       companyId: locationData.campusTBL.companyId,
  //       campusId: locationData.campusTBL.campusId,
  //     });
  //     setSelectedCompany(locationData.campusTBL.companyId);
  //     fetchCampusesByCompany(locationData.campusTBL.companyId);
  //   }
  // }, [locationData, form]);

  useEffect(() => {
    if (selectedCompany) {
      fetchCampusesByCompany(selectedCompany);
    } else {
      setCampusData([]);
    }
  }, [selectedCompany]);

  const fetchCompanyData = async () => {
    try {
      const result = await HTTPService.GETCompanyNames();
      setCompanyData(result);
    } catch (error) {
      console.error("Error fetching company data:", error);
    }
  };

  const getLocationData = async (locationId: number) => {
    try {
      const res = await HTTPService.GET(`/Location/ById?locationId=${locationId}`);
      if (res.status === 200 && res.data) {
        setLocationData(res.data);
        setSelectedCampus(res.data.campusId)
        setSelectedCompany(res.data.campusTBL.companyId);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: t('mistake'),
          detail: res.error?.message || t('error_fetching_data'),
          life: 3000
        });
      }
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: t('mistake'),
        detail: t('error_fetching_data'),
        life: 3000
      });
      console.error("Error fetching location data:", error);
    }
  };

  const fetchCampusesByCompany = async (companyId: number) => {
    try {
      const result = await HTTPService.GETCampusByCompany(companyId);
      setCampusData(result);
    } catch (error) {
      console.error("Error fetching campuses by company:", error);
      setCampusData([]);
    }
  };

  const updateLocation = (formData) => {
    const updatedLocation = {
      locationId: locationId,
      locationName: formData.locationName,
      companyId: selectedCompany|| userInfo.companyId,
      campusId: selectedCampus
    };

    HTTPService.PUT(`/Location/updateLocation`, updatedLocation)
      .then((res) => {
        if (res.status === 200) {
          toast.current?.show({
            severity: 'success',
            summary: t('successful'),	
            detail: t(res.data),
            life: 3000
          });
          window.location.replace("/admin/location-management");
        } else if(res.status === 450){
          toast.current?.show({
            severity: 'error',
            summary: t('mistake'),
            detail: t(res.data),
            life: 3000
          });
        } else if(res.status === 400){
          toast.current?.show({
            severity: 'error',
            summary: t('mistake'),
            detail: t(res.data),
            life: 3000
          });
        } else {
          toast.current?.show({
            severity: 'error',
            summary: t('mistake'),
            detail: t(res.data),
            life: 3000
          });
        }
      })
      .catch(() => {
        toast.current?.show({
          severity: 'error',
          summary: t('mistake'),
          detail: t('location_update_failed'),
          life: 3000
        });
      });
  };

  const handleCancel = () => {
    window.location.replace("/admin/location-management/");
  };

  const filterOption: SelectProps<any>['filterOption'] = (input, option) => {
    return (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase());
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={updateLocation}
      fields={[
        { name: ["locationName"], value:locationData?.locationName,},
        { name: ["campusId"], value: locationData?.campusTBL.campusId },
        { name: ["companyId"], value:locationData?.campusTBL.companyId },
        { name: ["barcode"], value:locationData?.barcode },
      ]}
    >
      <Card style={{ padding: "50px" }}>
        <h3>{t('update_location')}</h3>
        {userInfo?.role === UserRole.AltisAdmin ? (
          <Form.Item
            //name='companyId'
            label={t('Select company')}
            rules={[
              {
                required: true,
                message: t('please_select_a_company'),
              },
            ]}
          >
            <Select
              showSearch
              placeholder={t('company_name')}
              style={{ marginBottom: '6px' }}
              onChange={(value) => {
                setSelectedCompany(value);
                setSelectedCampus(undefined)
                form.setFieldsValue({ companyId: value });
              }}
              filterOption={filterOption}
              value={selectedCompany}
            >
              {companyData.map((company) => (
                <Select.Option key={company.companyId} value={company.companyId}>
                  {company.companyName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        ) : null}

        <Form.Item
          //name='campusId'
          label={t('campus')}
          rules={[{ required: true, message: t('select_campus') }]}
        >
          <Select placeholder={t('campus')}    
            value={selectedCampus}
              onChange={(value) => {
                setSelectedCampus(value)
              }}>
            {campusData.map(row => (
              <Select.Option key={row.campusId} value={row.campusId}>
                {row.campusName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="locationName"
              label={t('location')}
              rules={[
                {
                  required: true,
                  message: t('please_input_your_location'),
                  whitespace: true,
                },
              ]}
            >
              <Input style={{ borderRadius: 0 }} placeholder={t('location')}  value={locationData?.locationName}
            onChange={(e) =>
            setLocationData((prevData) => ({
              ...prevData,
              locationName: e.target.value,
            }))
          } />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name='barcode'
          label={t('barcode')}
        >
          <Input
            placeholder={t('barcode')}
            style={{borderRadius:'0'}}
            value={locationData?.barcode}
            readOnly
          />
        </Form.Item>

        <Form.Item>
    <Row gutter={[4, 4]} justify="end" align="middle">
        <Col xs={12} sm={4}>
            <Button
                block
                onClick={handleCancel}
                type="primary"
                style={{
                    height: 64,
                    backgroundColor: 'transparent',
                    color: '#1890ff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 10px',
                    border: 'none',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                    e.currentTarget.style.color = '#40a9ff';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.color = '#1890ff';
                }}
            >
                <CloseOutlined style={{ fontSize: 20 }} />
                <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                    {t('cancel')}
                </span>
            </Button>
        </Col>
        <Col xs={12} sm={4}>
            <Button
                block
                htmlType="submit"
                type="primary"
                style={{
                    height: 64,
                    backgroundColor: 'transparent',
                    color: '#1890ff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 10px',
                    border: 'none',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                    e.currentTarget.style.color = '#40a9ff';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.color = '#1890ff';
                }}
            >
                <SyncOutlined style={{ fontSize: 20 }} />
                <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                    {t('Update')}
                </span>
            </Button>
        </Col>
    </Row>
</Form.Item>
      </Card>
    </Form>
  );
};

export default UpdateLocation;
